// Mutable in-memory fixture store for `USE_FIXTURES` mode.
//
// All read hooks pull from the JSON when `USE_FIXTURES` is true. Write hooks
// (assignDbs, addDBRoute, deleteDBRoute, packVisibleItems, unpackVisibleItems,
// itemsQuantityStatusV3) bypass the API and mutate this store directly so
// the executive sees their actions reflected after the next reload.
//
// We deep-clone the JSON once at module load so the bundled fixture stays
// pristine (Metro caches it) and the store is the only thing that mutates.

import mockAssignedDbs from "./mockGetAssignedDbs.json";
import mockCrateCxItems from "./mockCrateCxItems.json";

const deepClone = (v) => JSON.parse(JSON.stringify(v));

const state = {
  assignedDbs: deepClone(mockAssignedDbs),
  // map: crate_id -> getCrateCxItems response
  crateItems: deepClone(mockCrateCxItems),
};

export const getAssignedDbsState = () => state.assignedDbs;

export const getCrateItemsState = (crateId) =>
  state.crateItems[crateId] || null;

// ── Writes ──────────────────────────────────────────────────────────────

const updateTpEverywhere = (cx_id, mutator) => {
  const root = state.assignedDbs;
  const collections = [
    Array.isArray(root?.data?.TP) ? root.data.TP : [],
    Array.isArray(root?.data?.DC) ? root.data.DC : [],
    Array.isArray(root?.data?.CRATE) ? root.data.CRATE : [],
  ];
  collections.forEach((coll) => {
    coll.forEach((card) => {
      if (!Array.isArray(card?.tp_list)) return;
      card.tp_list.forEach((tp) => {
        if (String(tp?.user_id) === String(cx_id)) mutator(tp);
      });
    });
  });
};

export const assignDbsFixture = ({ tps = [], cp_db_id, route_id }) => {
  const dbName = (() => {
    const found = (state.assignedDbs?.cp_dbs || []).find(
      (d) => String(d.cp_db_id) === String(cp_db_id),
    );
    return found?.db_name || "";
  })();

  tps.forEach(({ cx_id }) => {
    if (!cx_id) return;
    updateTpEverywhere(cx_id, (tp) => {
      if (route_id === "UNASSIGN") {
        tp.cp_db_id = "";
        tp.db_name = "";
        tp.status = "UNASSIGNED";
      } else if (route_id === "MISROUTE") {
        tp.cp_db_id = "MISROUTE";
        tp.db_name = "";
        tp.status = "MISROUTE";
      } else {
        tp.cp_db_id = cp_db_id;
        tp.db_name = dbName;
        tp.status = "ASSIGNED";
        if (route_id) tp.route_id = route_id;
      }
    });
  });

  // Recount unassigned per locality so LocalityCard chips refresh.
  const dcs = state.assignedDbs?.data?.DC || [];
  dcs.forEach((dc) => {
    const tps2 = dc?.tp_list || [];
    dc.unassigned_tp_count = tps2.filter(
      (t) => !t?.cp_db_id || t.cp_db_id === "UNASSIGN",
    ).length;
  });
};

export const addDBRouteFixture = ({ db_id, route_id, route_name }) => {
  const db = (state.assignedDbs?.cp_dbs || []).find(
    (d) => String(d.cp_db_id) === String(db_id),
  );
  if (!db) return;
  if (!Array.isArray(db.routes)) db.routes = [];
  if (db.routes.some((r) => String(r.route_id) === String(route_id))) return;
  db.routes.push({ route_id, route_name });
};

export const deleteDBRouteFixture = ({ db_id, route_id }) => {
  const db = (state.assignedDbs?.cp_dbs || []).find(
    (d) => String(d.cp_db_id) === String(db_id),
  );
  if (!db || !Array.isArray(db.routes)) return;
  db.routes = db.routes.filter((r) => String(r.route_id) !== String(route_id));
};

export const updateDBRouteFixture = ({ db_id, route_id, route_name }) => {
  const db = (state.assignedDbs?.cp_dbs || []).find(
    (d) => String(d.cp_db_id) === String(db_id),
  );
  if (!db || !Array.isArray(db.routes)) return;
  const r = db.routes.find((x) => String(x.route_id) === String(route_id));
  if (r) r.route_name = route_name;
};

const findCrateForItems = (crateId) => state.crateItems[crateId];

export const packVisibleItemsFixture = ({ crate_id, order_item_ids = [] }) => {
  const crate = findCrateForItems(crate_id);
  if (!crate) return;
  const ids = new Set(order_item_ids.map(Number));
  (crate.crate_data || []).forEach((section) => {
    (section.items || []).forEach((it) => {
      if (ids.has(Number(it.order_item_id)) && !it.packed_at) {
        it.packed_at = new Date().toISOString();
      }
    });
  });
  recomputeCrateCounts(crate);
  recomputeCrateInAssignedDbs(crate_id);
};

export const unpackVisibleItemsFixture = ({
  crate_id,
  order_item_ids = [],
}) => {
  const crate = findCrateForItems(crate_id);
  if (!crate) return;
  const ids = new Set(order_item_ids.map(Number));
  (crate.crate_data || []).forEach((section) => {
    (section.items || []).forEach((it) => {
      if (ids.has(Number(it.order_item_id))) it.packed_at = null;
    });
  });
  recomputeCrateCounts(crate);
  recomputeCrateInAssignedDbs(crate_id);
};

export const itemsQuantityStatusV3Fixture = ({
  order_item_id,
  diff = {},
  type,
  issue_images = {},
}) => {
  // Find the item across all crates.
  const crateIds = Object.keys(state.crateItems);
  for (let i = 0; i < crateIds.length; i += 1) {
    const crate = state.crateItems[crateIds[i]];
    const sections = crate?.crate_data || [];
    for (let j = 0; j < sections.length; j += 1) {
      const items = sections[j].items || [];
      const item = items.find(
        (it) => Number(it.order_item_id) === Number(order_item_id),
      );
      if (item) {
        // Apply diff to current_state.
        Object.entries(diff).forEach(([reason, qty]) => {
          item.current_state = item.current_state || {};
          const next = Number(item.current_state[reason] || 0) + Number(qty);
          item.current_state[reason] = Math.max(0, next);
        });
        // Detect dominant reason.
        const reasons = Object.keys(diff);
        const dominantReason = reasons.find((r) => Number(diff[r]) > 0);
        if (type === "MARKING") {
          if (dominantReason) {
            item.is_issue = true;
            item.issue_reason = dominantReason;
            item.issue_images = issue_images;
          } else {
            // Removal: any reason with negative diff resets the issue.
            const removed = reasons.find((r) => Number(diff[r]) < 0);
            if (removed) {
              item.is_issue = false;
              item.issue_reason = null;
              item.issue_images = {};
            }
          }
        }
        return;
      }
    }
  }
};

export const addToBagFixture = ({ cx_id, new_bags = [] }) => {
  // Update cxBagsList across all crates.
  Object.values(state.crateItems).forEach((crate) => {
    (crate.cxBagsList || []).forEach((row) => {
      if (String(row.cx_id) === String(cx_id)) {
        row.bags_list = new_bags.slice();
      }
    });
  });
};

const recomputeCrateCounts = (crate) => {
  let total = 0;
  let unpacked = 0;
  (crate.crate_data || []).forEach((section) => {
    (section.items || []).forEach((it) => {
      total += Number(it.quantity || 0);
      if (!it.packed_at) unpacked += Number(it.quantity || 0);
    });
  });
  crate.total_item_count = total;
  crate.unpacked_item_count = unpacked;
};

// Update packed_tps on the corresponding crate inside data.CRATE so
// derivations (Ready DBs, Overall Progress) refresh after a pack/unpack.
const recomputeCrateInAssignedDbs = (crate_id) => {
  const crateRef = (state.assignedDbs?.data?.CRATE || []).find(
    (c) => String(c.crate_id) === String(crate_id),
  );
  if (!crateRef) return;
  const crateItems = state.crateItems[crate_id];
  if (!crateItems) return;
  // Approximate: count cxs whose every item has packed_at set.
  const cxItemMap = new Map();
  (crateItems.crate_data || []).forEach((section) => {
    (section.items || []).forEach((it) => {
      const cxId = String(it.user_id || "");
      if (!cxId) return;
      const entry = cxItemMap.get(cxId) || { total: 0, packed: 0 };
      entry.total += 1;
      if (it.packed_at) entry.packed += 1;
      cxItemMap.set(cxId, entry);
    });
  });
  let packedCxs = 0;
  cxItemMap.forEach((entry) => {
    if (entry.packed === entry.total) packedCxs += 1;
  });
  crateRef.packed_tps = packedCxs;
};
